import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, Search, Sparkles } from "lucide-react";
import { DOCS_MARKDOWN } from "../lib/docsMarkdown";
import { Navigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../auth/AuthProvider";

// Private API reference for the Orange Trail Ads API, styled after modern
// developer docs: grouped sidebar with search, a quickstart that launches a
// real campaign, per-resource endpoint indexes, an "On this page" rail, and
// collapsible parameter references for the heavy bodies.

type RefRow = [string, string, string];

const NAV: ReadonlyArray<readonly [string, ReadonlyArray<readonly [string, string]>]> = [
  ["Get started", [
    ["quickstart", "Quickstart"],
    ["concepts", "Core concepts"],
  ]],
  ["Campaigns", [
    ["campaigns", "Ad campaigns"],
    ["ad-groups", "Ad groups"],
    ["ads", "Ads"],
    ["files", "Files"],
    ["social", "Social accounts"],
  ]],
  ["Tracking", [
    ["pixel", "Pixel & websites"],
    ["capi", "Server-side events (CAPI)"],
    ["reporting", "Reporting"],
  ]],
  ["Resources", [
    ["conventions", "Conventions & errors"],
  ]],
];

// Endpoint rows per section, shared by the index tables, the "On this page"
// rail, and sidebar search.
const REF: Record<string, RefRow[]> = {
  campaigns: [
    ["GET", "/ad_campaigns", "List campaigns. first, after, direction."],
    ["GET", "/ad_campaigns/{id}", "Retrieve one, with delivery and attribution metrics."],
    ["POST", "/ad_campaigns", "Create a draft campaign."],
    ["PATCH", "/ad_campaigns/{id}", "Update fields; status: \"active\" launches a draft."],
    ["DELETE", "/ad_campaigns/{id}", "Delete a campaign."],
  ],
  "ad-groups": [
    ["GET", "/ad_groups", "List. Filter with ad_campaign_id."],
    ["GET", "/ad_groups/{id}", "Retrieve one."],
    ["POST", "/ad_groups", "Create inside a campaign."],
    ["PATCH", "/ad_groups/{id}", "Update budget, targeting, schedule."],
    ["GET", "/ad_groups/targeting_options", "Search targeting categories for detailed_targeting. Requires platform=meta and search."],
    ["POST", "/ad_groups/estimate_reach", "Estimate audience size for a targeting spec. The body requires platform: meta."],
    ["DELETE", "/ad_groups/{id}", "Delete an ad group."],
  ],
  ads: [
    ["GET", "/ads", "List. Filter with ad_group_id or ad_campaign_id."],
    ["GET", "/ads/{id}", "Retrieve one."],
    ["POST", "/ads", "Create an ad in an ad group."],
    ["PATCH", "/ads/{id}", "Update creative, copy, destination."],
    ["DELETE", "/ads/{id}", "Delete an ad."],
  ],
  files: [
    ["POST", "/files", "Reserve an upload: { filename, byte_size?, multipart? }. Returns the upload URL(s)."],
    ["POST", "/files/{id}/complete", "Finalize a multipart upload with the part ETags."],
    ["GET", "/files/{id}", "Check processing; use the file in an ad once ready."],
    ["GET", "/files", "List uploaded files."],
  ],
  social: [
    ["GET", "/social_accounts", "The Facebook Pages and Instagram profiles connected to this ad account (sacc_…)."],
  ],
  pixel: [
    ["POST", "/events/validate_pixel", "Check that the pixel is firing: { url? } reads a page for the snippet, or omit url to check from received events."],
    ["GET", "/events/pulse", "A live feed of the most recent events the pixel is sending."],
  ],
  capi: [
    ["POST", "/events", "Send a server-side conversion event with match keys and attribution context."],
    ["GET", "/events", "Read raw events: a person's journey or the account's recent activity."],
  ],
  reporting: [
    ["GET", "/stats", "List every metric with its unit and the properties it can break down by."],
    ["GET", "/stats/{metric}", "Time series for a metric. Ads: ad_spend, ad_delivery. Traffic metrics cover your website."],
    ["GET", "/ad_reports", "Daily delivery report rows, when you prefer report-shaped data."],
  ],
  conventions: [
    ["400", "Bad request", "Invalid or missing parameters; the message says which."],
    ["401", "Unauthorized", "Missing, malformed, or revoked API key."],
    ["404", "Not found", "Unknown object, another account's object, or an endpoint outside this API."],
    ["409", "Conflict", "The action conflicts with the object's state, e.g. editing a non-writable campaign."],
    ["429", "Rate limited", "Slow down and retry with backoff."],
  ],
};

// Whop's Shopify custom-pixel script (monorepo public-docs), verbatim except
// formatting: runs inside Shopify's Customer-events sandbox, forwards its
// standard events to the pixel's conversion endpoint.
const SHOPIFY_PIXEL_SCRIPT = `const WHOP_ACCOUNT_ID = "biz_XXXXXXXXXXXX";

const WHOP_ENDPOINT = "https://t.whop.tw/conversions";

const TRACKED_EVENTS = new Set([
  "product_viewed", "collection_viewed", "search_submitted", "cart_viewed",
  "product_added_to_cart", "product_removed_from_cart", "checkout_started",
  "checkout_contact_info_submitted", "checkout_address_info_submitted",
  "checkout_shipping_info_submitted", "payment_info_submitted", "checkout_completed",
]);

const MAX_EVENT_NAME_LENGTH = 34;

const WHOP_STANDARD_EVENTS = new Set([
  "page", "lead", "contact", "schedule", "submit_application",
  "complete_registration", "view_content", "add_to_cart",
]);

// Map onto standard event names wherever Shopify's event has a real
// equivalent: a standard name skips the character-limit problem entirely,
// since only custom names get prefixed with your account tag. Everything else
// falls through to a shopify_-prefixed custom name; Shopify's own names for
// the three checkout steps are too long once prefixed, so they're shortened.
const EVENT_NAME_OVERRIDES = {
  page_viewed: "page",
  product_viewed: "view_content",
  product_added_to_cart: "add_to_cart",
  checkout_completed: "purchase",
  checkout_contact_info_submitted: "shopify_checkout_contact",
  checkout_address_info_submitted: "shopify_checkout_address",
  checkout_shipping_info_submitted: "shopify_checkout_shipping",
};

const TRACKED_QUERY_PARAMS = [
  "utm_id", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "fbclid", "gclid", "ttclid", "li_fat_id", "msclkid", "twclid", "rdt_cid",
  "sccid", "wbraid", "gbraid", "ig_sid",
];

function compact(object) {
  return Object.fromEntries(
    Object.entries(object).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  );
}

async function readCookie(name) {
  try {
    return (await browser.cookie.get(name)) || null;
  } catch {
    return null;
  }
}

async function readCookies() {
  const [fbp, fbc, ga, ttp] = await Promise.all([
    readCookie("_fbp"), readCookie("_fbc"), readCookie("_ga"), readCookie("_ttp"),
  ]);
  return { fbp, fbc, ga, ttp };
}

function readQueryParameters(href) {
  if (!href) return {};
  try {
    const url = new URL(href);
    return compact(
      Object.fromEntries(
        TRACKED_QUERY_PARAMS.map((name) => [name, url.searchParams.get(name)]),
      ),
    );
  } catch {
    return {};
  }
}

function readTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

function buildContext(event, cookies) {
  const documentContext = event.context?.document;
  const navigatorContext = event.context?.navigator;
  const screen = event.context?.window?.screen;
  const href = documentContext?.location?.href;

  return compact({
    user_agent: navigatorContext?.userAgent,
    language: navigatorContext?.language,
    timezone: readTimezone(),
    screen_resolution: screen ? \`\${screen.width}x\${screen.height}\` : null,
    fbp: cookies.fbp,
    fbc: cookies.fbc,
    ga: cookies.ga,
    ttp: cookies.ttp,
    ...readQueryParameters(href),
  });
}

function getAnonymousId(clientId) {
  if (!clientId) return null;
  const anonymousId = \`shopify_\${clientId}\`;
  if (anonymousId.length > 64) {
    console.error("Shopify client ID is too long");
    return null;
  }
  return anonymousId;
}

function getPixelEventName(shopifyEventName) {
  const name = EVENT_NAME_OVERRIDES[shopifyEventName] || \`shopify_\${shopifyEventName}\`;
  if (!WHOP_STANDARD_EVENTS.has(name) && name.length > MAX_EVENT_NAME_LENGTH) {
    console.error("Event name too long, Meta will drop it", name, name.length);
  }
  return name;
}

function getEventId(event, checkout) {
  if (event.name === "checkout_completed") {
    const orderOrCheckoutId = checkout?.order?.id || checkout?.token || event.id;
    return \`shopify:purchase:\${orderOrCheckoutId}\`;
  }
  return \`shopify:\${event.name}:\${event.id}\`;
}

function buildUser(event, checkout) {
  const anonymousId = getAnonymousId(event.clientId);
  if (!anonymousId) return null;

  const address = checkout?.shippingAddress || checkout?.billingAddress || {};
  const firstName = address.firstName || null;
  const lastName = address.lastName || null;

  return compact({
    anonymous_id: anonymousId,
    external_id: checkout?.order?.customer?.id || null,
    email: checkout?.email || null,
    phone: checkout?.phone || address.phone || null,
    first_name: firstName,
    last_name: lastName,
    name: [firstName, lastName].filter(Boolean).join(" ") || null,
    city: address.city || null,
    state: address.provinceCode || address.province || null,
    postal_code: address.zip || null,
    country: address.countryCode || address.country || null,
  });
}

async function sendPixelEvent(payload) {
  try {
    const response = await fetch(WHOP_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    });
    const responseBody = await response.text();
    if (!response.ok) {
      console.error("Pixel event failed", { status: response.status, response: responseBody, payload });
      return;
    }
    console.log("Pixel event sent", payload.event_name, responseBody);
  } catch (error) {
    console.error("Pixel request failed", payload.event_name, error);
  }
}

async function handleShopifyEvent(event) {
  const checkout = event.data?.checkout;
  const user = buildUser(event, checkout);
  if (!user) {
    console.warn("Shopify event missing client ID", event.name);
    return;
  }

  const cookies = await readCookies();
  const isPurchase = event.name === "checkout_completed";

  const payload = compact({
    account_id: WHOP_ACCOUNT_ID,
    event_name: getPixelEventName(event.name),
    event_id: getEventId(event, checkout),
    event_time: event.timestamp,
    action_source: event.name === "page_viewed" ? null : "website",
    url: event.context?.document?.location?.href || null,
    referrer_url: event.context?.document?.referrer || null,
    title: event.context?.document?.title || null,
    user,
    context: buildContext(event, cookies),
  });

  if (isPurchase && checkout?.totalPrice?.amount != null) {
    payload.value = Number(checkout.totalPrice.amount);
    const currency = checkout.totalPrice.currencyCode || checkout.currencyCode;
    if (currency) {
      payload.currency = currency.toLowerCase();
    }
  }

  await sendPixelEvent(payload);
}

analytics.subscribe("page_viewed", handleShopifyEvent);

analytics.subscribe("all_standard_events", async (event) => {
  if (event.name === "page_viewed" || !TRACKED_EVENTS.has(event.name)) {
    return;
  }
  await handleShopifyEvent(event);
});`;

const ON_PAGE_EXTRAS: Record<string, string[]> = {
  quickstart: ["1. Verify your key", "2. Create a campaign", "3. Create an ad group", "4. Upload a creative", "5. Create the ad & launch"],
  concepts: ["Scoped keys", "Headers", "Object model", "Lifecycle actions"],
  pixel: ["Install the snippet", "Install on a site builder", "Shopify", "WebinarJam", "Calendly & iClosed", "Standard events", "Custom events", "Verify the install"],
  capi: ["Send an event", "Match keys", "Deduplication", "Instant-form leads"],
};

function slugify(method: string, path: string): string {
  return `${method}-${path}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function CodeBlock({ code, lang = "bash" }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }).catch(() => undefined);
  };
  return <div className="docs-code">
    <div className="docs-code__bar">
      <span className="docs-code__lang">{lang}</span>
      <button type="button" onClick={copy} aria-label="Copy code">{copied ? <Check /> : <Copy />}{copied ? "Copied" : "Copy"}</button>
    </div>
    <pre>{code}</pre>
  </div>;
}

function EndpointIndex({ rows }: { rows: RefRow[] }) {
  return <table className="docs-index">
    <tbody>
      {rows.map(([method, path, description]) => <tr key={`${method}${path}`} id={slugify(method, path)}>
        <td><span className={`docs-method docs-method--${method.toLowerCase()}`}>{method}</span></td>
        <td><code>{path}</code></td>
        <td>{description}</td>
      </tr>)}
    </tbody>
  </table>;
}

function Fields({ label, rows }: { label: string; rows: RefRow[] }) {
  return <div className="docs-fields">
    <h4 className="docs-fields__title">{label}</h4>
    <div className="docs-params">
      {rows.map(([name, type, description]) => {
        const required = /required/i.test(type);
        const typeClean = type.replace(/,?\s*required/i, "").trim();
        return <div className="docs-param" key={name}>
          <div className="docs-param__head">
            <code>{name}</code>
            {typeClean && <span className="docs-param__type">{typeClean}</span>}
            {required && <span className="docs-param__req">required</span>}
          </div>
          <p>{description}</p>
        </div>;
      })}
    </div>
  </div>;
}

function Step({ number, title, children, code }: { number: number; title: string; children: React.ReactNode; code: string }) {
  return <div className="docs-step">
    <div className="docs-step__copy">
      <span className="docs-step__number">{number}</span>
      <div><strong>{title}</strong><p>{children}</p></div>
    </div>
    <CodeBlock code={code} />
  </div>;
}

export function ApiDocsPage() {
  const { session, booting } = useAuth();
  const [active, setActive] = useState<string>("quickstart");
  const [query, setQuery] = useState("");
  const [aiCopied, setAiCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const copyForAi = () => {
    void navigator.clipboard.writeText(DOCS_MARKDOWN).then(() => {
      setAiCopied(true);
      window.setTimeout(() => setAiCopied(false), 1800);
    }).catch(() => undefined);
  };

  // Scroll spy: highlight the section currently in view in the side nav.
  useEffect(() => {
    if (!session) return;
    const headings = bodyRef.current?.querySelectorAll("section[id]");
    if (!headings || headings.length === 0) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: "-90px 0px -65% 0px" });
    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [session]);

  const needle = query.trim().toLowerCase();
  const nav = useMemo(() => {
    if (!needle) return NAV;
    return NAV
      .map(([group, items]) => [group, items.filter(([id, label]) =>
        label.toLowerCase().includes(needle)
        || (REF[id] || []).some(([method, path]) => path.toLowerCase().includes(needle) || method.toLowerCase() === needle))] as const)
      .filter(([, items]) => items.length > 0);
  }, [needle]);

  if (booting) return <main className="callback-page"><Logo /><div className="loading-orbit" /><h1>Loading the API reference…</h1></main>;
  if (!session) return <Navigate to="/login" replace />;

  const onPage = REF[active] || [];
  const extras = ON_PAGE_EXTRAS[active] || [];

  return <main className="docs-page">
    <header className="docs-header">
      <a className="docs-brand" href="/app" aria-label="Back to the dashboard"><img src="/brand/logo.webp" alt="Orange Trail" /></a>
      <span className="docs-header__title">Ads API</span>
      <button className="button button--secondary button--small docs-ai-copy" type="button" onClick={copyForAi}>{aiCopied ? <Check /> : <Sparkles />}{aiCopied ? "Copied for AI" : "Copy for AI"}</button>
      <a className="button button--secondary button--small" href="/app/settings/developer">Manage API keys</a>
    </header>

    <div className="docs-layout docs-layout--rail">
      <nav className="docs-toc" aria-label="Sections">
        <label className="docs-search"><Search /><input value={query} placeholder="Search the API…" aria-label="Search the API reference" onChange={(event) => setQuery(event.target.value)} /></label>
        {nav.map(([group, items]) => <div className="docs-nav-group" key={group}>
          <span className="docs-toc__label">{group}</span>
          {items.map(([id, label]) => <a key={id} href={`#${id}`} className={active === id ? "is-active" : ""}>{label}</a>)}
        </div>)}
        {nav.length === 0 && <p className="docs-search__empty">Nothing matches. Try an endpoint path like /events.</p>}
      </nav>

      <article className="docs-body" ref={bodyRef}>
        <div className="docs-hero">
          <span className="docs-eyebrow">API reference</span>
          <h1>Orange Trail Ads API</h1>
          <p>Everything the dashboard does, over HTTPS: create campaigns, target audiences, upload creatives, launch ads, track conversions, and read delivery and attribution.</p>
          <div className="docs-hero__facts">
            <div><small>Base URL</small><code>https://orangetrail.app/api/v1</code></div>
            <div><small>Auth</small><code>Authorization: Bearer otk_live_…</code></div>
          </div>
        </div>

        <section id="quickstart">
          <h2>Quickstart: launch a campaign</h2>
          <p className="docs-section-lead">Five requests take you from nothing to a live ad. Create an API key in <a href="/app/settings/developer">Settings → Developer</a> first.</p>
          <p className="docs-note docs-note--warn"><strong>Billing is set up in the dashboard, not the API.</strong> Before a campaign can launch or spend, add the payment method under <a href="/app/settings/billing">Settings → Billing</a> (and connect an identity under <a href="/app/settings/identity">Settings → Identity</a>). Launch and spend requests without active billing fail with <code>409</code>.</p>
          <p className="docs-note"><Sparkles aria-hidden="true" style={{ width: 14, height: 14, verticalAlign: "-2px" }} /> <strong>Working with an AI agent?</strong> Use <em>Copy for AI</em> in the header to copy this whole reference as Markdown, or point your agent at <a href="https://orangetrail.app/api/v1/docs.md" target="_blank" rel="noreferrer">https://orangetrail.app/api/v1/docs.md</a>: the whole reference as plain Markdown, no authentication needed.</p>
          <Step number={1} title="Verify your key" code={`curl https://orangetrail.app/api/v1/me \\
  -H "Authorization: Bearer otk_live_..."

# { "ad_account_id": "biz_XXXX", "name": "Your ad account", ... }`}>
            <code>/me</code> returns your ad account ID: the identifier you will see on every object.
          </Step>
          <Step number={2} title="Create a draft campaign" code={`curl -X POST https://orangetrail.app/api/v1/ad_campaigns \\
  -H "Authorization: Bearer otk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Summer sale",
    "objective": "sales",
    "platform": "meta"
  }'

# => { "id": "adcamp_XXXX", "status": "draft", ... }`}>
            Campaigns start as drafts, so nothing spends yet. With no budget fields, each ad group carries its own budget.
          </Step>
          <Step number={3} title="Create an ad group with budget and targeting" code={`curl -X POST https://orangetrail.app/api/v1/ad_groups \\
  -H "Authorization: Bearer otk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "ad_campaign_id": "adcamp_XXXX",
    "budget_amount": 50,
    "budget_type": "daily",
    "optimization_goal": "conversions",
    "conversion_event": "purchase",
    "conversion_location": "website",
    "regions": { "include": { "countries": ["US"] } },
    "demographics": { "minimum_age": 21 },
    "placements": "automatic"
  }'`}>
            The ad group owns budget, optimization, and targeting. <code>placements: "automatic"</code> lets the network place the ads.
          </Step>
          <Step number={4} title="Upload a creative" code={`curl -X POST https://orangetrail.app/api/v1/files \\
  -H "Authorization: Bearer otk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "filename": "hero.jpg" }'

# => { "id": "file_XXXX", "upload_url": "https://..." }
# PUT the binary to upload_url, then poll GET /files/file_XXXX`}>
            Upload the binary straight to the returned URL, then wait for the file to finish processing.
          </Step>
          <Step number={5} title="Create the ad and launch" code={`curl -X POST https://orangetrail.app/api/v1/ads \\
  -H "Authorization: Bearer otk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "ad_group_id": "adset_XXXX",
    "creatives": [{ "id": "file_XXXX" }],
    "primary_texts": ["Our biggest sale of the year."],
    "headlines": ["Up to 50% off"],
    "call_to_action": "shop_now",
    "url": "https://yourstore.com/sale",
    "social_accounts": [{ "id": "sacc_XXXX" }]
  }'

# Launch: flip the draft campaign live
curl -X PATCH https://orangetrail.app/api/v1/ad_campaigns/adcamp_XXXX \\
  -H "Authorization: Bearer otk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "status": "active" }'`}>
            <code>sacc_…</code> is your Facebook Page from <code>GET /social_accounts</code>. Setting the campaign's status to <code>active</code> submits it for review and pushes it live.
          </Step>
        </section>

        <section id="concepts">
          <h2>Core concepts</h2>
          <div className="docs-concepts">
            <div><strong>Scoped keys</strong><p>Each key belongs to one ad account. Every request acts as that account: you never send an account identifier, and one you send is ignored. Your <strong>ad account ID</strong> (<code>biz_…</code>) appears in responses and reports.</p></div>
            <div><strong>Headers</strong><p><code>Authorization: Bearer otk_live_…</code> on every request. <code>Content-Type: application/json</code> on writes. Optional <code>Idempotency-Key</code> makes any write safe to retry. No version header: Orange Trail pins the API version.</p></div>
            <div><strong>Object model</strong><p>A <em>campaign</em> (<code>adcamp_</code>) holds the objective. Its <em>ad groups</em> (<code>adset_</code>) hold budget and targeting. Their <em>ads</em> (<code>ad_</code>) hold creative and copy. Every level supports pause, unpause, and duplicate.</p></div>
            <div><strong>Lifecycle actions</strong><p>Campaigns, ad groups, and ads all expose <code>POST …/{"{id}"}/pause</code>, <code>POST …/{"{id}"}/unpause</code>, and <code>POST …/{"{id}"}/duplicate</code>. They behave identically at each level and are not repeated below.</p></div>
          </div>
        </section>

        <section id="campaigns">
          <h2>Ad campaigns</h2>
          <EndpointIndex rows={REF.campaigns} />
          <Fields label="Create & update fields" rows={[
            ["title", "string, required", "Display name of the campaign."],
            ["objective", "enum, required", "awareness, traffic, engagement, leads, or sales."],
            ["platform", "enum, required", "The ad network. Use meta."],
            ["budget_optimization", "enum", "Who owns the budget: ad_campaign (one shared budget) or ad_group (default; each group budgets itself)."],
            ["budget_amount / budget_type", "number / enum", "The shared budget and whether it is daily or lifetime. Only when budget_optimization is ad_campaign."],
            ["bid_type", "enum", "minimum_cost (default behavior), average_target, or maximum_target; targets use desired_cost_per_result."],
            ["starts_at / ends_at", "ISO 8601", "Delivery window for campaign-budget campaigns."],
            ["special_ad_categories", "enum[]", "housing, employment, financial_products, politics. Adds targeting restrictions."],
            ["status", "enum (update only)", "Set to active to launch a draft. Running campaigns pause and resume via the lifecycle actions."],
          ]} />
          <p className="docs-note">Responses carry full metrics per campaign: <code>spend</code>, <code>impressions</code>, <code>clicks</code>, <code>reach</code>, <code>results</code>, <code>purchases</code>, <code>return_on_ad_spend</code>, plus <code>status</code>, <code>delivery_status</code>, and open <code>issues</code>.</p>
        </section>

        <section id="ad-groups">
          <h2>Ad groups</h2>
          <EndpointIndex rows={REF["ad-groups"]} />
          <Fields label="Create & update fields" rows={[
            ["ad_campaign_id", "string, required", "The campaign to create the group in."],
            ["budget_amount / budget_type", "number / enum", "This group's budget, daily or lifetime. Omit when the campaign owns the budget."],
            ["optimization_goal", "enum", "What delivery maximizes: conversions, link_clicks, landing_page_views, reach, impressions, engagement, conversations, video_views, lead_generation, …"],
            ["conversion_event", "string", "The pixel event optimized for: purchase, lead, add_to_cart, complete_registration, schedule, contact, or any custom pixel event name."],
            ["conversion_location", "enum", "Where the outcome happens: website, profile, messaging, on_ad, instant_forms, and combinations."],
            ["demographics", "object", "{ minimum_age, maximum_age, gender, automatic }. automatic: true enables platform-managed audience expansion."],
            ["regions", "object", "{ include, exclude } with countries (ISO codes), cities, zips, or custom_locations (coordinate + radius)."],
            ["detailed_targeting", "object", "{ interests, behaviors, demographics } category lists; find IDs via targeting_options. Send the complete intended state."],
            ["placements", "\"automatic\" | object[]", "automatic, or per-platform positions: facebook (feed, story, reels…), instagram (stream, reels, explore…), messenger, audience_network, threads."],
            ["audiences", "object", "{ include, exclude } saved audience IDs (adaud_…)."],
            ["devices / languages", "object / string[]", "OS and device-type targeting; languages as ISO 639 codes."],
            ["dynamic_creative", "boolean", "Let the platform mix creatives and copy. Creation only."],
            ["starts_at / ends_at", "ISO 8601", "Delivery window; omit ends_at to run until paused."],
          ]} />
        </section>

        <section id="ads">
          <h2>Ads</h2>
          <EndpointIndex rows={REF.ads} />
          <Fields label="Create & update fields" rows={[
            ["ad_group_id", "string", "The group to create the ad in; or pass ad_group, an inline ad group object, to create both at once."],
            ["creatives", "object[]", "Uploaded files: [{ id: \"file_…\", format?, crop? }]. Two or more entries without formats become a carousel (2-10, in order)."],
            ["primary_texts / headlines / descriptions", "string[]", "Copy variants shown on the ad."],
            ["call_to_action", "enum", "shop_now, learn_more, sign_up, subscribe, contact_us, download, get_offer, book_now, …"],
            ["url", "string", "Destination URL. Its query string is split into url_parameters automatically."],
            ["url_parameters", "object", "Extra query parameters; attribution keys (utm_source, wacid, …) are reserved."],
            ["social_accounts", "object[]", "The identities the ad runs under: [{ id: \"sacc_…\" }] from GET /social_accounts."],
            ["existing_post_id / post_source", "string / enum", "Promote an existing Facebook or Instagram post instead of creatives."],
            ["lead_form / lead_form_id", "object / string", "Instant lead form, when the group's conversion_location is an instant-forms destination."],
          ]} />
        </section>

        <section id="files">
          <h2>Files</h2>
          <EndpointIndex rows={REF.files} />
          <p className="docs-note">Upload the binary with a <code>PUT</code> to the returned <code>upload_url</code>, sending every header from the response&rsquo;s <code>upload_headers</code> verbatim. Use <code>multipart: true</code> above ~100MB (5MB parts, required past 5GB).</p>
        </section>

        <section id="social">
          <h2>Social accounts</h2>
          <EndpointIndex rows={REF.social} />
          <p className="docs-note">Connect or create identities in the dashboard under Settings → Identity; pass their entries in an ad's <code>social_accounts</code>.</p>
        </section>

        <section id="pixel">
          <h2>Pixel & websites</h2>
          <p className="docs-section-lead">The pixel measures page views on your website, links visitors to their purchases, and attributes conversions back to your ads with first-party data. Install it once on every page of your funnel.</p>
          <h3>Install the snippet</h3>
          <p>Paste this inside the <code>&lt;head&gt;</code> of every page: landing pages, advertorials, checkouts, thank-you pages. Replace the scope with your <strong>ad account ID</strong> from <a href="/app/settings/developer">Settings → Developer</a>.</p>
          <CodeBlock lang="html" code={`<script>
!function(w,d,s,u,n,a,b){if(w[n])return;a=w[n]={q:[],t:+new Date,s:[],o:u,
track:function(){a.q.push([+new Date].concat([].slice.call(arguments)))},
setScope:function(){a.s=[].slice.call(arguments).filter(function(x){return typeof x==="string"});
a.q.push([+new Date,"setScope"].concat(a.s))},
scope:function(){var c=[].slice.call(arguments);return{track:function(){
a.q.push([+new Date].concat([].slice.call(arguments)).concat([{__scope:c}]))}}}};
b=d.createElement(s);b.async=1;b.src=u+"/s.js";
d.getElementsByTagName(s)[0].parentNode.insertBefore(b,d.getElementsByTagName(s)[0])
}(window,document,"script","https://t.whop.tw","whop");
whop.setScope("biz_XXXXXXXXXXXX");
whop.track("page");
</script>`} />
          <h3>Install on a site builder</h3>
          <p>Most funnel builders have a slot for header or tracking code. Paste the snippet there, not into a page's text or content block, so it loads on every page. For these platforms the standard snippet works as-is; only the slot's location differs.</p>
          <Fields label="Where the snippet goes" rows={[
            ["GoHighLevel", "header code", "Sites → Funnels (or Websites) → open it → Settings → Tracking Code → Header Tracking. Setting it at the funnel level covers every page inside it."],
            ["ClickFunnels", "header code", "Funnel Settings → Custom Code → Head Code. Use the Sites app's global Header Code to cover every funnel at once."],
            ["Kajabi", "header code", "Settings → Site Details → Page Scripts → Header Page Scripts."],
            ["WordPress", "plugin", "WordPress does not expose the <head> by default. Install a header-script plugin (WPCode or Insert Headers and Footers) and paste into its Header field. This also covers Elementor, Divi, and other builders."],
            ["WooCommerce", "plugin", "Same as WordPress: a header-script plugin's Header field. Covers the shop, product, and cart pages."],
          ]} />
          <p className="docs-note">On a builder with no header slot at all, add the same snippet through Google Tag Manager as a Custom HTML tag that fires on all pages. Shopify, WebinarJam, Calendly, and iClosed need more than the standard snippet: their full walkthroughs follow.</p>

          <h3>Shopify</h3>
          <p>Shopify's checkout runs in a sandboxed environment that ignores scripts pasted into <code>theme.liquid</code>, so a normal install never sees it. Shopify's own <strong>Custom Pixel</strong> feature is the way around that: it runs in every part of the store, checkout included, and reports standard events like <code>checkout_completed</code> directly. The script below listens for those events and forwards each one, with the order's value, the buyer's contact and address fields, and the ad-click IDs from the landing URL.</p>
          <p>In Shopify go to <strong>Settings → Customer events → Add custom pixel</strong>, name it, and paste this into the pixel's code editor (replace the account ID with yours from <a href="/app/settings/developer">Settings → Developer</a>). Then click <strong>Save</strong> and <strong>Connect</strong>: Shopify won't send any events to an unconnected pixel.</p>
          <CodeBlock lang="javascript" code={SHOPIFY_PIXEL_SCRIPT} />
          <p className="docs-note"><strong>Keep the purchase mapping.</strong> Automatic server-side purchase tracking only covers checkouts on the ads platform itself. A Shopify order is processed entirely inside Shopify, so nothing reports it unless this script's <code>checkout_completed</code> (sent as <code>purchase</code>) does. It is not a duplicate.</p>
          <p>To confirm: visit a live storefront page, check the dashboard's <a href="/app/tracking">Pixel & websites</a> tab for your domain, then place a test order and confirm a purchase lands too.</p>

          <h3>WebinarJam</h3>
          <p>In WebinarJam open your webinar, go to <strong>Integrations → Integrate a 3rd Party Tracking System → Embed Your Custom Tracking Code</strong>. WebinarJam gives you a separate tracking field per funnel step; each one gets a snippet:</p>
          <Fields label="What goes in each field" rows={[
            ["Registration Form Tracking", "plain snippet", "The form is a popup with its own context, separate from the page around it. The visitor's email, name, and phone fields live here, so this placement is what lets the pixel's automatic field capture see them."],
            ["Registration Page Tracking", "snippet + lead listener", "The standard snippet plus the listener below, which fires the lead event the instant registration completes, before the redirect to the thank-you page happens."],
            ["Post-Registration Thank You Page", "plain snippet", "A backup page-view record."],
            ["Live room / Replay page", "plain snippet (optional)", "Add if you want visits to those steps tracked."],
          ]} />
          <p>The Registration Page field gets the standard snippet followed by this listener:</p>
          <CodeBlock lang="javascript" code={`window.addEventListener("message", function (e) {
  if (e.origin !== "https://event.webinarjam.com") return;

  let data;
  try {
    data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
  } catch {
    return;
  }

  if (data.func === "redirect" && typeof data.url === "string" && data.url.indexOf("/thank-you") !== -1) {
    whop.track("lead");
  }
});`} />
          <p className="docs-note">Running registration on your own site instead of WebinarJam's hosted page? Install the snippet on that page directly; WebinarJam's tracking fields only cover its own hosted pages.</p>

          <h3>Calendly & iClosed</h3>
          <p>Neither booking tool can tell your website when someone books, so bookings can't be tracked the normal way. The fix is the same for both: send bookers to a thank-you page on your own site after they book, and record the booking there. The booker's name and email ride along in the redirect URL and are picked up automatically.</p>
          <Fields label="The four steps" rows={[
            ["1. Booking page", "snippet", "Install the standard snippet on the page where people actually book (your landing page, or wherever the calendar is embedded). Without it you see only the final booking, no page views or ad clicks."],
            ["2. Thank-you page", "snippet", "Build a simple confirmation page on your own site and install the standard snippet there too. Every page in the funnel needs its own copy."],
            ["3. Redirect", "platform setting", "Calendly: event type → Edit → More options → Confirmation page → Redirect to an external site, and turn ON “Pass event details to your redirected page” (that switch is what carries the booker's name and email). iClosed: event settings → Confirmation Page → your thank-you URL."],
            ["4. Record the booking", "schedule snippet", "Right under the pixel snippet on the thank-you page, add the tracker below."],
          ]} />
          <CodeBlock lang="html" code={`<script>
var params = new URLSearchParams(window.location.search);
whop.track("schedule", {
  email: params.get("invitee_email") || undefined,
  name: params.get("invitee_full_name") || undefined,
});
</script>`} />
          <p className="docs-note">Embedding Calendly's scheduler on your own page (Share → Add to Website) instead of linking out? You can skip the redirect: keep the snippet on that page and listen for <code>calendly.event_scheduled</code> via <code>window.addEventListener("message", ...)</code>, firing <code>whop.track("schedule")</code> when it arrives. That confirms the booking but carries no name or email (Calendly keeps those out of the client-side event), so pass details you collected earlier in the funnel yourself, or use the redirect method.</p>
          <h3>Standard events</h3>
          <p>Page views, checkout views, purchases, subscriptions, and trials are tracked automatically. Add extra <code>track</code> calls only for funnel steps that happen on your own site. Every event optionally takes <code>value</code> and <code>currency</code>.</p>
          <Fields label="Standard event names" rows={[
            ["lead", "event", "A visitor submits contact info or an opt-in form."],
            ["schedule", "event", "A visitor books a call or appointment."],
            ["submit_application", "event", "A visitor submits an application."],
            ["contact", "event", "A visitor starts a conversation or contact request."],
            ["complete_registration", "event", "A signup or registration finishes."],
            ["view_content", "event", "A visitor views a key page or piece of content."],
            ["add_to_cart", "event", "A visitor adds an item to a cart."],
          ]} />
          <h3>Custom events</h3>
          <CodeBlock lang="javascript" code={`whop.track("lead");                                      // a standard event
whop.track("schedule", { value: 50, currency: "USD" });  // optionally with a value
whop.track("watched_vsl");                               // or your own event name
whop.track("quiz_completed", { value: 25, currency: "USD" });`} />
          <p className="docs-note">Custom names show up in your reporting and in <code>conversion_event</code> targeting on ad groups. Keep names short and stable: names under about 34 characters also forward to Meta as custom conversions.</p>
          <h3>Verify the install</h3>
          <EndpointIndex rows={REF.pixel} />
          <p>The dashboard's <a href="/app/tracking">Pixel & websites</a> tab shows live status and every domain reporting events. Website analytics (visits by hostname, page, device, and source) are available through the <a href="#reporting">traffic metrics</a>.</p>
        </section>

        <section id="capi">
          <h2>Server-side events (CAPI)</h2>
          <p className="docs-section-lead">The conversions API is the server-side twin of the pixel: send purchases, leads, and any custom event from your backend so conversions that happen off the page (or after ad blockers) still attribute to your ads and improve delivery optimization.</p>
          <EndpointIndex rows={REF.capi} />
          <h3>Send an event</h3>
          <CodeBlock code={`curl -X POST https://orangetrail.app/api/v1/events \\
  -H "Authorization: Bearer otk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "event_name": "lead",
    "event_time": "2026-08-29T14:03:00Z",
    "event_id": "lead_84921",
    "value": 25,
    "currency": "usd",
    "url": "https://yourstore.com/thanks",
    "action_source": "website",
    "user": {
      "email": "jane@example.com",
      "phone": "+15551234567",
      "external_id": "cust_84921",
      "first_name": "Jane",
      "country": "US"
    },
    "context": {
      "fbp": "fb.1.1725900000.1234567890",
      "fbc": "fb.1.1725900000.AbCdEfGh",
      "ip_address": "203.0.113.7",
      "user_agent": "Mozilla/5.0 ..."
    }
  }'`} />
          <Fields label="Event fields" rows={[
            ["event_name", "string, required", "A standard pixel event: lead, schedule, submit_application, contact, complete_registration, view_content, add_to_cart. Any other name (or custom with custom_name) records as a custom event under that name. A purchase sent here is an external purchase (a sale on your own checkout): it must include a positive value or it is rejected, it counts toward purchase-optimized ad groups, and it is forwarded to Meta as a standard Purchase. Checkouts on the platform itself are tracked automatically; do not re-send them."],
            ["custom_name", "string", "Your event name when event_name is custom. Up to 35 characters."],
            ["event_time", "ISO 8601", "When the event occurred. Defaults to now."],
            ["event_id", "string", "Your identifier for deduplication: send the same event_id from the browser and the server and the event counts once."],
            ["value / currency", "number / enum", "Monetary value and ISO 4217 currency (usd, eur, gbp, …)."],
            ["url", "string", "The page where the event occurred."],
            ["action_source", "enum", "Where it originated: website, app, email, phone_call, chat, physical_store, system_generated, business_messaging, other."],
          ]} />
          <Fields label="user: match keys" rows={[
            ["email / phone", "string", "The strongest match keys. Send raw values; hashing is handled downstream."],
            ["external_id", "string", "Your own customer ID: keeps matches stable across email changes."],
            ["first_name / last_name / name", "string", "Name fields to strengthen matching."],
            ["city / state / postal_code / country", "string", "Address fields to strengthen matching."],
            ["birthdate / gender", "string / enum", "YYYY-MM-DD; male or female."],
            ["anonymous_id / linked_anonymous_id", "string", "The pixel's anonymous visitor IDs, to join server events with browser sessions (also across iframes)."],
            ["user_id / member_id / membership_id / username", "string", "Platform identifiers when you know them."],
          ]} />
          <Fields label="context: attribution signals" rows={[
            ["fbp / fbc / fbclid", "string", "Facebook browser pixel cookie, click cookie, and click ID: forward these for Meta attribution."],
            ["gclid / gbraid / wbraid", "string", "Google click IDs."],
            ["ttclid / ttp", "string", "TikTok click ID and pixel ID."],
            ["msclkid / twclid / li_fat_id / sccid / rdt_cid", "string", "Microsoft, X, LinkedIn, Snapchat, and Reddit click IDs."],
            ["ip_address / user_agent", "string", "The visitor's IP and browser user agent, from your server logs."],
            ["utm_source / utm_medium / utm_campaign / utm_content / utm_term / utm_id", "string", "UTM parameters from the landing session."],
            ["ad_campaign_id / ad_set_id / ad_id", "string", "Attribute directly to a campaign, ad group, or ad when you know it."],
            ["ga / language / timezone / screen_resolution / fingerprint", "string", "Additional analytics and device signals."],
          ]} />
          <p className="docs-note"><strong>Deduplication:</strong> when the pixel and your server both report the same conversion, give both the same <code>event_id</code>. Reading back: <code>GET /events?identifier=jane@example.com</code> shows a person's full journey; filter with <code>event</code> and <code>source</code> (e.g. <code>ext:meta:*</code> for ad-driven events).</p>
          <h3 id="instant-form-leads">Instant-form leads</h3>
          <p>Leads collected by Meta instant forms on your ads are recorded as <code>lead</code> events on the ad account, with the person's contact details and every form answer attached. They are not a separate resource: read them from <code>GET /events</code>.</p>
          <CodeBlock lang="bash" code={`curl "https://orangetrail.app/api/v1/events?event=pixel.lead&from=2026-09-01T00:00:00Z&to=2026-09-01T12:00:00Z" \\
  -H "Authorization: Bearer otk_live_..."`} />
          <Fields label="Each lead event" rows={[
            ["event_name", "string", "Always pixel.lead for a form lead."],
            ["event_id", "string", "Meta's id for the submission. Stable across polls, so use it to skip leads you already processed."],
            ["event_time", "ISO 8601", "When the person submitted the form."],
            ["user", "object", "email, phone, first_name, last_name, name, city, country, as the person typed them into the form."],
            ["questions", "array", "Every question on the form with its answer: { question, answer, key, type, options }. Custom questions appear here too."],
            ["person_id", "string", "The ad network's identity for the person, for GET /events?identifier=... to see their full journey."],
          ]} />
          <p className="docs-note"><strong>Timing:</strong> instant-form leads sync in from Meta every 15 minutes, so a lead appears here within about 15 minutes of the submission. To forward leads to a CRM, poll this endpoint every few minutes with <code>from</code> set to your previous check and push each new <code>event_id</code>. Leads also count in the <code>leads</code> metric on campaigns, ad groups, and ads.</p>
        </section>

        <section id="reporting">
          <h2>Reporting</h2>
          <EndpointIndex rows={REF.reporting} />
          <Fields label="Stats query parameters" rows={[
            ["from / to", "date | ISO 8601, required", "The range. Dates expand to full days; timestamps are used exactly."],
            ["interval", "enum", "minute, hour, day (default), week, month, year."],
            ["breakdown_by", "string", "Split each point by a property. breakdown_by=metric on ad_delivery returns spend, impressions, and clicks together. Traffic metrics break down by source, hostname, page, and device_type."],
            ["hostname / page / device_type / source", "string", "Filters on the traffic metrics: one website, one page, one device type, or one visit source (whop_ads, direct, or a utm_source)."],
            ["time_zone", "string", "IANA zone for bucketing, e.g. America/New_York. Defaults to UTC."],
            ["convert_to", "string", "Display currency for money metrics. Defaults to usd."],
          ]} />
          <CodeBlock code={`# Ad spend by day
curl "https://orangetrail.app/api/v1/stats/ad_spend?from=2026-08-01&to=2026-08-28" \\
  -H "Authorization: Bearer otk_live_..."

# Website visits split by page (the traffic metric is page_visits; see GET /stats for all keys)
curl "https://orangetrail.app/api/v1/stats/page_visits?from=2026-08-01&to=2026-08-28&breakdown_by=page" \\
  -H "Authorization: Bearer otk_live_..."`} />
        </section>

        <section id="conventions">
          <h2>Conventions & errors</h2>
          <div className="docs-concepts">
            <div><strong>Pagination</strong><p>Lists return <code>{`{ data, page_info }`}</code>. Page with <code>first</code> and <code>after</code> (from <code>page_info.end_cursor</code>); some lists also take <code>before</code> and <code>direction</code>.</p></div>
            <div><strong>Errors</strong><p>Failures return <code>{`{ error: { message, type, code? } }`}</code>; authentication failures from Orange Trail return <code>{`{ message }`}</code>.</p></div>
          </div>
          <EndpointIndex rows={REF.conventions} />
          <footer className="docs-footer">Questions? <a href="mailto:hello@orangetrail.io">hello@orangetrail.io</a></footer>
        </section>
      </article>

      <aside className="docs-onpage" aria-label="On this page">
        <span className="docs-toc__label">On this page</span>
        {extras.map((label) => <span className="docs-onpage__item" key={label}>{label}</span>)}
        {onPage.map(([method, path]) => <a key={`${method}${path}`} href={`#${slugify(method, path)}`}>
          <span className={`docs-method docs-method--${method.toLowerCase()}`}>{method}</span>
          <code>{path}</code>
        </a>)}
      </aside>
    </div>
  </main>;
}
